import { useContext, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import {
  ArrowBack,
  HelpOutlineOutlined,
  HistoryOutlined,
  LogoutOutlined,
  PersonOutlineOutlined,
  SettingsOutlined,
  ShieldOutlined,
  StoreOutlined,
} from '@mui/icons-material'
import { AppBar, Box, Button, Divider, Drawer, Grid, IconButton, Toolbar, Typography } from '@mui/material'

import protectMobileLogo from '../../images/protect-icon-logo-2.svg'
import { AuthenticationContext } from '../../providers/auth-provider'
import { DashboardContext } from '../../providers/dashboard-provider'
import { NavContext } from '../../providers/nav-provider'
import AccountDetailsModal from '../account-details-modal'
import ChangePasswordModal from '../change-password-modal'
import ContactDetailsModal from '../contact-details-modal'
import Nav from '../nav/nav'
import NeedHelpModal from '../need-help-modal'
import SettingsModal from '../settings-modal'

import useStyles from './index.styles'

const MoreMenu = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const classes = useStyles

  const navigate = useNavigate()
  const location = useLocation()
  const { selectedFolderId: folderId } = useContext(DashboardContext)

  const [showSupportModal, setShowSupportModal] = useState<boolean>(false)
  const [showContactDetailsModal, setShowContactDetailsModal] = useState(false)
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [showAccountDetailsModal, setShowAccountDetailsModal] = useState(false)

  const { ownUser } = useContext(AuthenticationContext)
  const { setMoreMenuOpen } = useContext(NavContext)

  return (
    <Drawer anchor="left" open={open} onClose={onClose} sx={classes.moreMenuDrawer}>
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar>
          <IconButton size="large" edge="start" color="inherit" onClick={onClose} aria-label="close">
            <ArrowBack />
          </IconButton>

          <Typography variant="h6" sx={classes.moreMenuTitle}>
            More
          </Typography>
        </Toolbar>
      </AppBar>

      <Grid container={true} sx={classes.moreMenuContainer}>
        <Grid>
          <Box sx={classes.moreMenuSectionHeader}>
            <Typography variant="subtitle2" sx={classes.moreMenuSectionTitle}>
              Personal Account
            </Typography>
          </Box>

          <Nav.List>
            <Nav.Item
              label="Contact details"
              icon={<PersonOutlineOutlined color="secondary" />}
              selected={false}
              onClick={() => setShowContactDetailsModal(true)}
            />

            <Nav.Item label="Security" icon={<ShieldOutlined color="secondary" />} selected={false} onClick={() => setShowChangePasswordModal(true)} />

            <Nav.Item label="Settings" icon={<SettingsOutlined color="secondary" />} selected={false} onClick={() => setShowSettingsModal(true)} />
          </Nav.List>

          <Divider />

          <Box sx={classes.moreMenuSectionHeader}>
            <Typography variant="subtitle2" sx={classes.moreMenuSectionTitle}>
              Account Settings
            </Typography>
          </Box>

          <Nav.List>
            <Nav.Item label="Account details" icon={<StoreOutlined color="secondary" />} selected={false} onClick={() => setShowAccountDetailsModal(true)} />

            <Nav.Item
              label="Order history"
              icon={<HistoryOutlined color="secondary" />}
              selected={false}
              onClick={() => {
                setMoreMenuOpen(false)
                navigate(`/dashboard/${folderId}/order-history`, { state: location.state })
              }}
            />
          </Nav.List>

          <Divider />

          <Box sx={classes.moreMenuSectionHeader}>
            <Typography variant="subtitle2" sx={classes.moreMenuSectionTitle}>
              Other
            </Typography>
          </Box>

          <Nav.List>
            <Nav.Item
              label="Help & Support"
              icon={<HelpOutlineOutlined color="secondary" />}
              selected={false}
              onClick={() => {
                setMoreMenuOpen(false)
                navigate(`/dashboard/${folderId}/help`, { state: location.state })
              }}
            />
          </Nav.List>
          <Button
            variant="contained"
            color="secondary"
            disableElevation
            component="a"
            href="https://meshifyprotect.app.link/download-app"
            target="_blank"
            rel="noopener noreferrer"
            sx={classes.downloadAppButton}
          >
            <img src={protectMobileLogo} alt="" width={16} height={16} />
            Download Mobile App
          </Button>
        </Grid>

        <Grid>
          <Nav.List>
            <Nav.Item label="Sign out" icon={<LogoutOutlined color="secondary" />} selected={false} onClick={() => navigate(`/logout`)} />
          </Nav.List>
        </Grid>
      </Grid>

      <NeedHelpModal isOpen={showSupportModal} onClose={() => setShowSupportModal(false)} />

      {ownUser && folderId && (
        <ContactDetailsModal isOpen={showContactDetailsModal} onClose={() => setShowContactDetailsModal(false)} user={ownUser} selectedFolderId={folderId} />
      )}

      <ChangePasswordModal open={showChangePasswordModal} onClose={() => setShowChangePasswordModal(false)} />

      <SettingsModal open={showSettingsModal} onClose={() => setShowSettingsModal(false)} />

      {folderId && <AccountDetailsModal isOpen={showAccountDetailsModal} onClose={() => setShowAccountDetailsModal(false)} folderId={folderId} />}
    </Drawer>
  )
}

export default MoreMenu
